import { type TypedUseSelectorHook, useSelector } from 'react-redux'
import { type OrderedMap } from 'immutable'

export type Comment<
  CommentType extends
    | 'comment'
    | 'post'
    | 'file'
    | 'facebook'
    | 'twitter'
    | 'instagram'
    | 'photo'
    | 'link'
    | 'video'
    | 'question'
    | 'chat'
    | 'speaker-chat',
> = {
  avatar: string
  comment_id: string
  object_id: string
  object_type: string
  comment_type: CommentType
  comment_status: '' | 'answered' | 'dismissed'
  promoted_p: number
  name: string
  first_name: string
  facebook_link: string
  twitter_link: string
  email: string
  label: string
  url: string
  pretty_date: string
  pretty_time: string
  comment_time: string
  short_date: string
  truncated_name: string
  creation_date_ansi: string
  object_url: string
  uuid: string
  full_name: string
  content_text: string
  content: string
  dateFormatted: string
}

interface WebinarCommentsList<
  CommentType extends 'chat' | 'speaker-chat' | 'question',
> {
  comments: OrderedMap<string, Comment<CommentType>>
  isLoading: boolean
  totalCount: string | number
  currentPage: number
  isInfiniteLoading: boolean
  hasMoreElements: boolean
  isWatchingRealtime: boolean
  includeBotGreeting: boolean
  hasSentFirstComment: boolean
}

interface WebinarChatRoom<RoomType extends 'chat' | 'speaker-chat'> {
  questions: { comments: OrderedMap<string, Comment<'question'>> }
  chat: WebinarCommentsList<RoomType>
  shouldScroll: boolean
  scrollIntention: boolean
}

interface WebinarAudienceSidebar {
  isShowingSpeakers: boolean
  // TODO: complete
}

interface WebinarInfo {
  liveData: {
    name: string
    thumbnail_small_download: string
    live_date_epoch: string
    locale: string
    primary_timezone_live_date__date: string
    primary_timezone_live_date__time: string
  }
  webinarInfo: {
    current_viewers: string
    streaming_start_time: string
    broadcasting_start_time: string
  }
}

interface ReduxRootState {
  webinar: WebinarInfo
  'feature-webinar-sidebar-chat': WebinarChatRoom<'chat'>
  'feature-webinar-sidebar-speaker-chat': WebinarChatRoom<'speaker-chat'>
  'feature-webinar-audience-sidebar': WebinarAudienceSidebar
}

const useAppSelector: TypedUseSelectorHook<ReduxRootState> = useSelector

export type { ReduxRootState }

export { useAppSelector }
